import { isCancelledError, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import { isDecisionMakerQuery, toDecisionMakerLookupQuery } from '#src/app/pages/search/shared/api';
import type {
	DecisionMakerMatch,
	PetitionSearchParams,
	SearchResults,
	SearchResultsData,
} from '#src/app/pages/search/shared/api';
import {
	SEARCH_DECISION_MAKER_GRACE_MS,
	SEARCH_DECISION_MAKER_MATCHING,
	SEARCH_DECISION_MAKER_STALE_TIME_MS,
} from '#src/app/pages/search/shared/consts';

import { useSearchClientContext } from '../useSearchClientContext';
import { useSearchState } from '../useSearchState';

// Search results are short-lived but worth caching for back/forward and repeat queries. These
// per-query options deliberately override the global no-cache defaults (gcTime/staleTime: 0,
// refetchOnMount: 'always') so cached results aren't discarded/refetched on every navigation.
const STALE_TIME_MS = 120_000;
const GC_TIME_MS = 5 * 60_000;

const EMPTY_RESULTS: SearchResultsData & Pick<SearchResults, 'decisionMaker'> = {
	hits: [],
	nbHits: 0,
	nbPages: 0,
	query: '',
	topicFacets: [],
	decisionMaker: null,
};

function shouldFindDecisionMaker(params: PetitionSearchParams, isDmCardEnabled: boolean): boolean {
	return isDmCardEnabled && params.page === 0 && isDecisionMakerQuery(params.query, SEARCH_DECISION_MAKER_MATCHING);
}

function getDecisionMakerQueryKey(decisionMakersCollectionName: string, query: string): readonly string[] {
	return ['search-decision-maker', decisionMakersCollectionName, toDecisionMakerLookupQuery(query)];
}

/**
 * Resolves with null after `ms`, so a slow lookup can be raced without being cancelled
 */
async function resolveNullAfter(ms: number): Promise<null> {
	// oxlint-disable-next-line promise/avoid-new -- wrapping setTimeout is the one place a new promise is needed
	return new Promise((resolve) => {
		setTimeout(() => resolve(null), ms);
	});
}

/**
 * Results store, backed by react-query. Every caller shares one fetch + one cache entry keyed on
 * the URL-derived params, and re-renders only when results change (not when unrelated filters do).
 */
export function useSearchResults(): SearchResults {
	const { searchClient, collectionName, decisionMakersCollectionName } = useSearchClientContext();
	const params = useSearchState();
	const queryClient = useQueryClient();
	const { errorReporter } = useUtilityContext();
	const {
		data: {
			featureConfigs: { isDmCardEnabled },
		},
	} = useSearchPageData();

	const query = useQuery({
		queryKey: ['search-petitions', collectionName, decisionMakersCollectionName, params, isDmCardEnabled],
		queryFn: async () => {
			if (!shouldFindDecisionMaker(params, isDmCardEnabled)) {
				return { ...(await searchClient.searchPetitions(params)), decisionMaker: null };
			}

			// the lookup has its own cache entry, keyed on the query only, so filter/sort/page changes reuse it
			// and a slow lookup keeps running in the background for the next time this query is shown
			const decisionMakerQueryKey = getDecisionMakerQueryKey(decisionMakersCollectionName, params.query);
			const decisionMakerLookup = (async (): Promise<DecisionMakerMatch | null> => {
				try {
					return await queryClient.ensureQueryData({
						queryKey: decisionMakerQueryKey,
						queryFn: async ({ signal }) => searchClient.searchDecisionMaker(params.query, signal),
						staleTime: SEARCH_DECISION_MAKER_STALE_TIME_MS,
						gcTime: SEARCH_DECISION_MAKER_STALE_TIME_MS,
						retry: false,
					});
				} catch (error) {
					// the card is an extra: a failure must never break the petition results, but should be visible
					// (a cancelled lookup isn't a failure)
					if (!isCancelledError(error)) void errorReporter.report(error as Error, { severity: 'warning' });
					return null;
				}
			})();

			// a failed petition search doesn't cancel the lookup: other fetches of this query may be waiting on it
			const petitions = searchClient.searchPetitions(params);
			// the petitions wait for the lookup only briefly once they've arrived, so the card never delays them much
			const decisionMaker = await Promise.race([
				decisionMakerLookup,
				(async () => {
					await petitions;
					return resolveNullAfter(SEARCH_DECISION_MAKER_GRACE_MS);
				})(),
			]);
			return { ...(await petitions), decisionMaker };
		},
		placeholderData: keepPreviousData,
		staleTime: STALE_TIME_MS,
		gcTime: GC_TIME_MS,
		refetchOnMount: false,
	});

	const data = query.data ?? EMPTY_RESULTS;

	return {
		...data,
		// a lookup that missed the grace period still finishes into its own entry: use it when these cached results are
		// shown again (back button, same search). Read without subscribing, so it never pops in above results already shown.
		decisionMaker:
			data.decisionMaker ??
			(query.data && shouldFindDecisionMaker(params, isDmCardEnabled)
				? (queryClient.getQueryData<DecisionMakerMatch | null>(
						getDecisionMakerQueryKey(decisionMakersCollectionName, params.query),
					) ?? null)
				: null),
		isLoading: query.isFetching,
		isError: query.isError,
		isInitialLoad: query.isLoading,
	};
}
